/**
 * CampaignDetailPage — layout shell for the campaign-detail surface. Seeds
 * campaignId/slug and role capabilities into their stores (so tabs read from
 * stores, not props), fetches the campaign header, and renders the sidebar,
 * header, and active tab. Holds no business logic — mutations live in the tabs.
 */
import React from 'react';
import {
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  type CampaignPermissions,
} from '@discovr/features';
import { useCampaignDetail, useRosterCounts } from '@discovr/features';
import type { ContentSubmission, ScriptDoc } from '@discovr/types';
import type { NavItem } from '../components/common/AppSidebar';
import { CampaignNavSidebar } from '../components/common/CampaignNavSidebar';
import { useSidebarCollapse } from '../components/common/useSidebarCollapse';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { AppErrorBoundary } from '../components/common/AppErrorBoundary';
import { useBreadcrumbSlot } from '../components/common/useBreadcrumbSlot';
import { CampaignBreadcrumb, type Crumb } from './CampaignBreadcrumb';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { House } from '../design-system/icons';
import { useCampaignDeepLinkTab } from './useCampaignDeepLinkTab';
import { useCampaignDeepLinkPanel } from './useCampaignDeepLinkPanel';
import { useCampaignReportingDeepLink } from './useCampaignReportingDeepLink';
import { resolveCampaignShortCode } from './resolveCampaignShortCode';
import { ImportProgressPopup } from './components/CreatorsTab/ImportProgressPopup';
import { ReportExportNotifier } from '../campaign-reporting/ReportExportNotifier';
import type {
  HoldPostArgs,
  MetricsRecheckResponse,
  ProfileRecheckResponse,
  ReleasePostArgs,
  RollbackApprovalArgs,
  RollbackAcceptanceArgs,
  RollbackBrandApprovalArgs,
  RosterRowRef,
} from '@discovr/features';
import { TabNotFound } from './components/TabNotFound';
import { isDisabledLaneTab } from './utils/campaignPipeline';
import { resolveCampaignLandingTab } from './campaignLandingTab';
import { DEFAULT_CAMPAIGN_TAB, LEGACY_ACCESS_TAB, type CampaignSearch, type CampaignTab } from './search';
import type { Campaign, CampaignEditRequest, SubmitEditRequestPayload } from '@discovr/types';

// ─── Lazy tab imports ─────────────────────────────────────────────────────────

const CampaignDetailsTab = React.lazy(() => import('./components/CampaignDetailsTab').then((m) => ({ default: m.CampaignDetailsTab })));
const CampaignReportingTab = React.lazy(() => import('./components/CampaignReportingTab').then((m) => ({ default: m.CampaignReportingTab })));
const CreatorsTab = React.lazy(() => import('./components/CreatorsTab').then((m) => ({ default: m.CreatorsTab })));
const ConfirmedTab = React.lazy(() => import('./components/ConfirmedTab').then((m) => ({ default: m.ConfirmedTab })));
const ScriptsTab = React.lazy(() => import('./components/ScriptsTab').then((m) => ({ default: m.ScriptsTab })));
const ContentTab = React.lazy(() => import('./components/ContentTab').then((m) => ({ default: m.ContentTab })));
const LiveContentTab = React.lazy(() => import('./components/LiveContentTab').then((m) => ({ default: m.LiveContentTab })));
const CampaignSettingsTab = React.lazy(() => import('./components/CampaignSettingsTab').then((m) => ({ default: m.CampaignSettingsTab })));
const ShopifySettingsTab = React.lazy(() => import('./components/ShopifySettingsTab').then((m) => ({ default: m.ShopifySettingsTab })));

// ─── Lazy overlays ────────────────────────────────────────────────────────────

const CreatorInsightsDrawer = React.lazy(() => import('./components/insights/CreatorInsightsDrawer').then((m) => ({ default: m.CreatorInsightsDrawer })));
const ReviewThreadDrawer = React.lazy(() => import('./components/ReviewThreadDrawer').then((m) => ({ default: m.ReviewThreadDrawer })));

// ─── Skeletons ────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="space-y-6">
      {/* KPI row — matches KpiCard's real shape (Figma 1672:33334): bordered
          white card, stone-100 icon circle, skeleton only over the value. */}
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-[18px]">
            <div className="size-12 shrink-0 rounded-full bg-stone-100" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-7 w-20" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton className="h-5 w-40" />
      {/* Table — matches DataTable's card + row shape (avatar + text left,
          status-pill placeholder right), rows divided on a white card. */}
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TabSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}

// ─── Reporting sub-tab labels (mirror SUB_TABS in CampaignOverviewTab) ─────────
const REPORTING_SUB_LABELS: Record<string, string> = {
  overview: 'General Insights',
  sentiment: 'Sentiment Intelligence',
  audienceDynamics: 'Audience Dynamics',
  creatorContent: 'Creator Content',
  conversions: 'Conversions',
};

const FALLBACK_TAB_LABEL = 'Details';

/**
 * A URL can name a tab that isn't in the nav (e.g. a stale #shopify link when
 * Shopify is disabled); fall back to the default tab so a hidden one is never
 * rendered. `null` (tab not decided yet) stays `null`.
 */
function resolveVisibleTab(tab: CampaignTab | null, navItems: NavItem[]): CampaignTab | null {
  if (tab === null) return null;
  const isTabVisible = navItems.some((item) => item.id === tab);
  return isTabVisible ? tab : DEFAULT_CAMPAIGN_TAB;
}

function resolveTabLabel(tab: CampaignTab | null, navItems: NavItem[]): string | null {
  if (tab === null) return null;
  return navItems.find((item) => item.id === tab)?.label ?? FALLBACK_TAB_LABEL;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CampaignCapabilitiesInput {
  role: 'admin' | 'admin_member' | 'brand_owner' | 'brand_admin' | 'brand_member';
  /** True only for the Super Admin tier. Always omitted/false on the brand
   *  lane — see the store's own doc comment. */
  viewerIsSuperAdmin?: boolean;
  permissions: CampaignPermissions;
  /** Caller's effective per-campaign level (read|write|admin) for bespoke gating. */
  accessLevel?: 'read' | 'write' | 'admin' | null;
  /** Which app surface set these capabilities — see the store's own doc comment. */
  lane?: 'admin' | 'brand' | null;
  // Render props
  renderEmbeddedBrandSafetyAgent?: ((creator: Record<string, unknown>) => React.ReactNode) | null;
  renderEmbeddedContentReviewAgent?: ((creator: Record<string, unknown>, onClose: () => void) => React.ReactNode) | null;
  renderEmbeddedScriptReviewAgent?: ((creator: Record<string, unknown>, onClose: () => void) => React.ReactNode) | null;
  // Creator management
  onEditCreator?: ((row: Record<string, unknown>) => void) | null;
  approveCreatorAsBrand?: ((creator: Record<string, unknown>, deliverables: string[]) => Promise<void>) | null;
  rejectCreatorApproval?: ((creator: Record<string, unknown>, reason: string) => Promise<void>) | null;
  deleteCreatorFromCampaign?: ((creator: Record<string, unknown>, confirm: string) => Promise<void>) | null;
  selectCreatorDeliverable?: ((entryId: string, types: string[]) => Promise<void>) | null;
  reinstateCreator?: ((creator: Record<string, unknown>) => Promise<void>) | null;
  holdPost?: ((args: HoldPostArgs) => Promise<void>) | null;
  releasePost?: ((args: ReleasePostArgs) => Promise<void>) | null;
  rollbackApproval?: ((args: RollbackApprovalArgs) => Promise<void>) | null;
  rollbackAcceptance?: ((args: RollbackAcceptanceArgs) => Promise<void>) | null;
  rollbackBrandApproval?: ((args: RollbackBrandApprovalArgs) => Promise<void>) | null;
  moveCreatorToScriptReview?: ((creator: Record<string, unknown>, deliverables: string[], doc: ScriptDoc) => Promise<{ stage?: string } | void>) | null;
  approveCreatorAndGoLive?: ((creator: Record<string, unknown>, deliverables: string[], url: string) => Promise<{ stage?: string } | void>) | null;
  approveCreatorAndAddContent?: ((creator: Record<string, unknown>, deliverables: string[], url: string) => Promise<{ stage?: string } | void>) | null;
  // Script callbacks
  confirmScriptUpload?: ((entryId: string, doc: ScriptDoc) => Promise<void>) | null;
  /** Same route as confirmScriptUpload, explicit alias for the Confirmed-tab
   *  "add script one phase early" call sites. */
  onAddScript?: ((entryId: string, doc: ScriptDoc) => Promise<void>) | null;
  handleAdminReviewScript?: ((entryId: string, action: 'approve' | 'request_changes', feedback?: string) => Promise<void>) | null;
  handleRunAiScriptReview?: ((entryId: string) => Promise<void>) | null;
  // Content callbacks
  handleAdminSubmitContent?: ((entryId: string, payload: ContentSubmission) => Promise<void>) | null;
  handleAdminReviewContent?: ((entryId: string, action: 'approve' | 'request_changes', feedback?: string) => Promise<void>) | null;
  handleRunAiContentReview?: ((entryId: string) => Promise<void>) | null;
  handleAdminSetEta?: ((entryId: string, phase: 'script' | 'content' | 'live', eta: string | null) => Promise<void>) | null;
  handleSaveInsights?: ((entryId: string, insights: Record<string, string>) => Promise<void>) | null;
  // Approval modal
  confirmCreatorApproval?: ((candidate: Record<string, unknown>, deliverables: string[]) => Promise<void>) | null;
  // Campaign details / brief
  saveCampaignDetails?: ((data: Record<string, unknown>) => Promise<void>) | null;
  // Edit-request review flow
  canEditCampaign?: boolean;
  submitCampaignEditRequest?: ((payload: SubmitEditRequestPayload) => Promise<CampaignEditRequest>) | null;
  editCampaignDirectly?: ((payload: SubmitEditRequestPayload) => Promise<Campaign>) | null;
  reviewEditRequest?: {
    approve: (requestId: string, fieldPath: string) => Promise<CampaignEditRequest>;
    reject: (requestId: string, fieldPath: string, reason: string) => Promise<CampaignEditRequest>;
    apply: (requestId: string) => Promise<CampaignEditRequest>;
  } | null;
  /** Both: post one entry to a field's comment thread, at any status. */
  addFieldComment?: ((requestId: string, fieldPath: string, text: string) => Promise<CampaignEditRequest>) | null;
  // Reporting render prop
  renderReportingTab?: (() => React.ReactNode) | null;
  // Other
  handleConfirmRosterBrief?: ((entryId: string) => Promise<void>) | null;
  handleSetLiveUrl?: ((entryId: string, url: string) => Promise<void>) | null;
  /** Admin/ops: on-demand Official Audio Compliance recheck. null for brand. */
  recheckAudioCompliance?: ((entryId: string) => Promise<void>) | null;
  /** Admin: force-refresh one creator's Instagram profile. null for brand. */
  recheckCreatorProfile?: ((entryId: string) => Promise<ProfileRecheckResponse>) | null;
  /** Admin: force-refresh one creator's Instagram reel metrics. null for brand. */
  recheckCreatorMetrics?: ((entryId: string) => Promise<MetricsRecheckResponse>) | null;
  // Share-link (admin only): when true the campaign-detail surface exposes a
  // copy-share-link button that deep-links into the brand app rooted at
  // `shareBrandBaseUrl`. Brand leaves both unset → falsy → no share UI.
  enableSharing?: boolean;
  shareBrandBaseUrl?: string;
}

export interface CampaignDetailPageProps {
  /** Resolved Mongo ObjectID (from useResolvedCampaignId). */
  campaignId: string;
  /** URL slug — for SSE matching and cascade resets. */
  campaignSlug?: string;
  /** External loading override — true while the caller is still resolving
   * the campaign ID (slug resolver in flight). Prevents the "Campaign not
   * found" flash that would otherwise appear before campaignId is populated. */
  loading?: boolean;
  capabilities: CampaignCapabilitiesInput;
  navItems: NavItem[];
  /** Back link → campaigns list (col-2 nav header). */
  onNavigateBack: () => void;
  /** Campaigns-list route behind the "Campaign" breadcrumb crumb, so it is a real link rather than history back. */
  campaignsLink?: { href: string; onNavigate: () => void };
  onRefresh?: () => void;
  backLabel?: string;
  /** False while `navItems` can still change (scopes or profile resolving); no landing tab is chosen until true. */
  isNavReady: boolean;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CampaignDetailPage({
  campaignId,
  campaignSlug = '',
  loading: externalLoading = false,
  capabilities,
  navItems,
  onNavigateBack,
  campaignsLink,
  onRefresh,
  backLabel,
  isNavReady,
}: CampaignDetailPageProps) {
  const navigate = useNavigate();
  const router = useRouter();
  // Collapse state for the campaign-nav column (col-2). The main rail (col-1)
  // and the top bar are owned by the persistent AppLayout shell now, not here.
  const { collapsed: navCollapsed, toggleCollapsed: toggleNavCollapsed } = useSidebarCollapse(
    'discovr:campaign-nav-collapsed',
    { collapseOnNarrow: true },
  );

  // Redirect legacy #access-control / ?tab=access-control deep links to
  // settings?sub=access. The raw router location is reactive, so a legacy link
  // that arrives after mount is caught too.
  const { tab: searchTab } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const isLegacyLink = searchTab === LEGACY_ACCESS_TAB;
  React.useEffect(() => {
    if (!isLegacyLink) return;
    // `as never`: this router-agnostic package can't reference the app's
    // route tree; the schema validates `tab` at runtime.
    void navigate({
      to: '.',
      search: ((prev: Partial<CampaignSearch>) => ({
        ...prev,
        tab: 'settings',
        sub: 'access',
      })),
    } as never);
  }, [isLegacyLink, navigate]);

  // All tabs page-scroll; roster tables handle their own dual-scroll inside
  // RosterTableShell. The content region must NOT bound/overflow-hidden, or it
  // would kill both the page scroll and the sticky header.

  const {
    deepAnalysisCreator,
    convCreator,
    setCampaignId,
    setCampaignSlug,
    setCampaignShortCode,
    setCampaignPlatform,
    setCampaignPipeline,
  } = useCampaignUiStore();

  const { setCapabilities } = useCampaignCapabilitiesStore();
  const setImportProgress = useCampaignCapabilitiesStore((s) => s.setImportProgress);
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);
  const setImportWizardOpen = useCampaignCapabilitiesStore((s) => s.setImportWizardOpen);

  // ── Seed stores ─────────────────────────────────────────────────────────────
  // Run synchronously-equivalent effect — only when IDs/caps change.
  React.useEffect(() => {
    if (campaignId) setCampaignId(campaignId);
    // New campaign context: drop any import popup state carried over from the
    // previously-viewed campaign (the store is a global singleton and SPA nav
    // between two campaign pages reuses this component without a remount).
    setImportProgress(null);
    setImportResult(null);
    setImportWizardOpen(false);
  }, [campaignId, setCampaignId, setImportProgress, setImportResult, setImportWizardOpen]);

  React.useEffect(() => {
    setCampaignSlug(campaignSlug);
  }, [campaignSlug, setCampaignSlug]);

  // ── Campaign header + roster data (TanStack Query) ──────────────────────────
  // The per-tab roster rows serve only the creator deep-link panel, so they're
  // gated on a link being present (?panel / ?creator) to avoid wasted requests.
  const {
    panel: deepLinkPanel,
    creator: deepLinkCreator,
    video: rosterVideo,
    focus: rosterFocus,
    pill: rosterPill,
    page: rosterPage,
    sort_by: rosterSort,
  } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const hasCreatorDeepLink = !!deepLinkPanel || !!deepLinkCreator;
  const hasRosterLink = hasCreatorDeepLink || !!rosterVideo || !!rosterFocus || !!rosterPill || rosterPage !== undefined || !!rosterSort;

  const {
    campaign: campaignQuery,
    creators,
    finalized,
    scripts,
    content,
    live,
  } = useCampaignDetail(campaignId, { rosterEnabled: hasCreatorDeepLink });
  const { data: campaign, isLoading: queryLoading, error, refetch } = campaignQuery;
  // Same query the Creators/Confirmed tabs badge from, so it's deduped. Only the
  // no-link landing decision reads it.
  const { data: rosterCounts, isError: rosterCountsFailed } = useRosterCounts(campaignId ?? '', { enabled: !hasRosterLink });
  // A failed count request counts as zero, so the landing falls back to Creators instead of waiting forever.
  const landingCounts = rosterCounts ?? (rosterCountsFailed ? { creators_total: 0, finalized_total: 0 } : null);
  // Combine: outer loading (slug resolver in flight) OR our own query loading.
  const loading = externalLoading || queryLoading;

  // Tab is URL-driven (typed `?tab` search param); the store is mirrored
  // one-way (URL → store) inside the hook so other store readers stay in sync.
  // With no `?tab`, a campaign with live creators lands on Reporting; a
  // background refetch of a cached header never blanks the page.
  const isLandingBlocked = !isNavReady || isLegacyLink;
  const landingTab = isLandingBlocked
    ? null
    : resolveCampaignLandingTab({
        campaign,
        navItems,
        hasRosterLink,
        creatorsCount: landingCounts ? landingCounts.creators_total : null,
        confirmedCount: landingCounts ? landingCounts.finalized_total : null,
      });
  const { tab: rawTab, setTab: setActiveTab } = useCampaignDeepLinkTab(landingTab);
  const activeTab = resolveVisibleTab(rawTab, navItems);
  const isHiddenShopifyLink =
    isNavReady &&
    !!campaign &&
    !loading &&
    searchTab === 'shopify' &&
    !navItems.some((item) => item.id === 'shopify');
  React.useEffect(() => {
    if (!isHiddenShopifyLink) return;
    void navigate({
      to: '.',
      replace: true,
      search: ((prev: Partial<CampaignSearch>) => ({ ...prev, tab: 'settings', sub: 'integrations' })),
    } as never);
  }, [isHiddenShopifyLink, navigate]);
  // No tab yet while the nav resolves, or while the campaign's landing waits on
  // the roster counts: show the skeleton rather than a wrong tab.
  const isLandingPending = activeTab === null && (!isNavReady || (!!campaign && !isLegacyLink));

  // ── Seed capabilities ────────────────────────────────────────────────────────
  // Placed after `loading` is computed and depends on it, so it re-fires once
  // the campaign-detail fetch settles and picks up the real accessLevel/
  // permissions (campaignId resolves from a separate, earlier slug→ID lookup).
  //
  // Gate on `!loading && !error`, not just `!loading`: a query that settled
  // with an error also has loading:false, and the shared queryClient never
  // retries 4xx (a 401 racing token attachment right after sign-in settles it
  // to error with no retry). This keeps capabilitiesReady consistent with the
  // error screen the render gate below already shows.
  React.useEffect(() => {
    setCapabilities({
      role: capabilities.role,
      viewerIsSuperAdmin: capabilities.viewerIsSuperAdmin ?? false,
      permissions: capabilities.permissions,
      accessLevel: capabilities.accessLevel ?? null,
      capabilitiesReady: !loading && !error,
      lane: capabilities.lane ?? null,
      renderEmbeddedBrandSafetyAgent: capabilities.renderEmbeddedBrandSafetyAgent ?? null,
      renderEmbeddedContentReviewAgent: capabilities.renderEmbeddedContentReviewAgent ?? null,
      renderEmbeddedScriptReviewAgent: capabilities.renderEmbeddedScriptReviewAgent ?? null,
      onEditCreator: capabilities.onEditCreator ?? null,
      approveCreatorAsBrand: capabilities.approveCreatorAsBrand ?? null,
      rejectCreatorApproval: capabilities.rejectCreatorApproval ?? null,
      deleteCreatorFromCampaign: capabilities.deleteCreatorFromCampaign ?? null,
      selectCreatorDeliverable: capabilities.selectCreatorDeliverable ?? null,
      reinstateCreator: capabilities.reinstateCreator ?? null,
      holdPost: capabilities.holdPost ?? null,
      releasePost: capabilities.releasePost ?? null,
      rollbackApproval: capabilities.rollbackApproval ?? null,
      rollbackAcceptance: capabilities.rollbackAcceptance ?? null,
      rollbackBrandApproval: capabilities.rollbackBrandApproval ?? null,
      moveCreatorToScriptReview: capabilities.moveCreatorToScriptReview ?? null,
      approveCreatorAndGoLive: capabilities.approveCreatorAndGoLive ?? null,
      approveCreatorAndAddContent: capabilities.approveCreatorAndAddContent ?? null,
      onAddScript: capabilities.onAddScript ?? null,
      confirmScriptUpload: capabilities.confirmScriptUpload ?? null,
      handleAdminReviewScript: capabilities.handleAdminReviewScript ?? null,
      handleRunAiScriptReview: capabilities.handleRunAiScriptReview ?? null,
      handleAdminSubmitContent: capabilities.handleAdminSubmitContent ?? null,
      handleAdminReviewContent: capabilities.handleAdminReviewContent ?? null,
      handleRunAiContentReview: capabilities.handleRunAiContentReview ?? null,
      handleAdminSetEta: capabilities.handleAdminSetEta ?? null,
      handleSaveInsights: capabilities.handleSaveInsights ?? null,
      confirmCreatorApproval: capabilities.confirmCreatorApproval ?? null,
      saveCampaignDetails: capabilities.saveCampaignDetails ?? null,
      canEditCampaign: capabilities.canEditCampaign ?? false,
      submitCampaignEditRequest: capabilities.submitCampaignEditRequest ?? null,
      editCampaignDirectly: capabilities.editCampaignDirectly ?? null,
      reviewEditRequest: capabilities.reviewEditRequest ?? null,
      addFieldComment: capabilities.addFieldComment ?? null,
      renderReportingTab: capabilities.renderReportingTab ?? null,
      handleConfirmRosterBrief: capabilities.handleConfirmRosterBrief ?? null,
      handleSetLiveUrl: capabilities.handleSetLiveUrl ?? null,
      recheckAudioCompliance: capabilities.recheckAudioCompliance ?? null,
      recheckCreatorProfile: capabilities.recheckCreatorProfile ?? null,
      recheckCreatorMetrics: capabilities.recheckCreatorMetrics ?? null,
      enableSharing: capabilities.enableSharing ?? false,
      shareBrandBaseUrl: capabilities.shareBrandBaseUrl ?? null,
    });
    // Callback closures are covered by `capabilities.role` changing (a re-mount
    // for a different role ships a new object); `loading`/`error` are added so a
    // same-role loading→loaded/errored transition re-fires this effect and picks
    // up the real accessLevel/permissions/capabilitiesReady.
    // `renderReportingTab` is the exception: its closure carries the admin
    // app's report:export scope, which can resolve or change with the role
    // unchanged. Both apps memoise it, so it only re-fires on a real change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilities.role, capabilities.renderReportingTab, campaignId, loading, error]);

  // ── Creator-drawer deep link (?panel + ?focus) ──────────────────────────────
  // Drawers are keyed by roster-entry id. Per-tab rows let the reader follow a
  // creator across tabs; rowsReady (every roster query settled) lets the hook
  // strip an unresolvable deep link instead of waiting forever.
  const rosterRowsByTab = React.useMemo(
    () => ({
      creators: (creators.data?.entries ?? []) as unknown as RosterRowRef[],
      finalized: (finalized.data?.entries ?? []) as unknown as RosterRowRef[],
      scripts: (scripts.data?.entries ?? []) as unknown as RosterRowRef[],
      content: (content.data?.entries ?? []) as unknown as RosterRowRef[],
      live: (live.data?.entries ?? []) as unknown as RosterRowRef[],
    }),
    [creators.data, finalized.data, scripts.data, content.data, live.data],
  );
  const rosterRowsReady =
    !creators.isLoading &&
    !finalized.isLoading &&
    !scripts.isLoading &&
    !content.isLoading &&
    !live.isLoading;
  useCampaignDeepLinkPanel(rosterRowsByTab, rosterRowsReady);

  // Sync platform into store once campaign resolves so platform-aware
  // components can read it without drilling props.
  React.useEffect(() => {
    setCampaignPlatform(campaign?.platform ?? null);
  }, [campaign?.platform, setCampaignPlatform]);

  // Layout effect so children read the pipeline before first paint.
  React.useLayoutEffect(() => {
    setCampaignPipeline(campaign?.pipeline ?? null);
  }, [campaign?.pipeline, setCampaignPipeline]);


  // Sync the short-link campaign code once the header resolves. Campaigns
  // without a stored short_code fall back to buildCampCode(slug), so the store's
  // campaignShortCode is never empty (the pending-public-action campCode check
  // relies on strict equality with no fail-open).
  React.useEffect(() => {
    setCampaignShortCode(resolveCampaignShortCode(campaign?.short_code, campaignSlug));
  }, [campaign?.short_code, campaignSlug, setCampaignShortCode]);

  const handleRefresh = React.useCallback(() => {
    void refetch();
    onRefresh?.();
  }, [refetch, onRefresh]);

  // ── Breadcrumb → persistent top bar ───────────────────────────────────────
  // Publish "Home › Campaign › <name> › <tab> [› <reporting sub>]" to the shell
  // top bar; clear on unmount so leaving the campaign restores the route header.
  // Segments before the leaf are clickable jumps.
  const setBreadcrumb = useBreadcrumbSlot((s) => s.setNode);
  const { sub: reportingSub, setSub: setReportingSub } = useCampaignReportingDeepLink();
  const campaignName = campaign?.name ?? 'Campaign';
  // A script/content tab the pipeline dropped is a 404, not a silent fallback.
  const isDisabledLane = rawTab !== null && isDisabledLaneTab(rawTab, navItems, campaign?.pipeline);
  const tabLabel = isDisabledLane ? 'Not found' : resolveTabLabel(activeTab, navItems);
  const isReportingTab = activeTab === 'reporting';
  const reportingSubLabel =
    isReportingTab ? (REPORTING_SUB_LABELS[reportingSub ?? 'overview'] ?? 'General Insights') : null;
  const homeHref = router.buildLocation({ to: '/' } as never).href;
  const campaignHref = router.buildLocation({ to: '.', search: { tab: 'details' } } as never).href;
  React.useEffect(() => {
    const crumbs: Crumb[] = [
      { label: 'Home', icon: House, onClick: () => void navigate({ to: '/' } as never), href: homeHref },
      { label: 'Campaign', onClick: campaignsLink?.onNavigate ?? onNavigateBack, href: campaignsLink?.href },
      { label: campaignName, onClick: () => setActiveTab('details'), href: campaignHref, truncate: true },
    ];
    if (tabLabel !== null) {
      crumbs.push({ label: tabLabel, onClick: isReportingTab ? () => setReportingSub('overview') : undefined });
    }
    if (isReportingTab) {
      crumbs.push({ label: reportingSubLabel ?? 'General Insights' });
    }
    setBreadcrumb(<CampaignBreadcrumb crumbs={crumbs} />);
    return () => setBreadcrumb(null);
    // Handlers are stable in behavior; rebuild only when the visible labels change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, homeHref, campaignHref, campaignsLink?.href, activeTab, tabLabel, reportingSubLabel, setBreadcrumb]);

  return (
    <>
      <div className="flex w-full min-w-0 flex-1 font-sans">
        {/* Column 2 — per-campaign nav. Column 1 (main rail) + the top bar are
            owned by the persistent AppLayout shell, so this surface only renders
            the campaign nav + content. */}
        <CampaignNavSidebar
          items={navItems}
          activeTab={isDisabledLane ? rawTab : activeTab}
          onTabChange={setActiveTab}
          onBack={onNavigateBack}
          backLabel={backLabel}
          collapsed={navCollapsed}
          onToggleCollapse={toggleNavCollapsed}
        />

        {/* Host: fills remaining width; centers content at max 1200px (matching campaigns/home) */}
        <div className="flex min-w-0 flex-1 flex-col items-center">
        <div className="box-border flex w-full max-w-[1200px] flex-1 flex-col p-8">
            {/* !campaignId: slug resolver still in-flight (query disabled →
                data=undefined) would flash "Campaign not found." Treat as loading. */}
            {loading || !campaignId || isLandingPending ? (
              <PageSkeleton />
            ) : error ? (
              <Card className="p-10 text-center">
                <p className="mb-4 text-sm font-semibold text-red-500">
                  {error instanceof Error ? error.message : 'Failed to load campaign.'}
                </p>
                <Button variant="outline" onClick={handleRefresh}>
                  Retry
                </Button>
              </Card>
            ) : !campaign ? (
              <p className="text-sm text-muted-foreground">Campaign not found.</p>
            ) : (
              <div className="flex flex-1 flex-col gap-5">
                {isDisabledLane ? (
                  <TabNotFound onGoBack={() => setActiveTab('confirmed')} />
                ) : (
                <AppErrorBoundary>
                  <React.Suspense fallback={<TabSkeleton />}>
                    {activeTab === 'details' && <CampaignDetailsTab />}
                    {activeTab === 'reporting' && <CampaignReportingTab />}
                    {activeTab === 'creators' && <CreatorsTab />}
                    {activeTab === 'confirmed' && <ConfirmedTab />}
                    {/* X has no script stage, so the tab is dropped from the nav.
                        Guard the render too, or a stale deep link reaches an empty tab. */}
                    {activeTab === 'scripts' && <ScriptsTab />}
                    {activeTab === 'content' && <ContentTab />}
                    {activeTab === 'live-content' && <LiveContentTab />}
                    {activeTab === 'settings' && <CampaignSettingsTab />}
                    {activeTab === 'shopify' && <ShopifySettingsTab />}
                  </React.Suspense>
                </AppErrorBoundary>
                )}
              </div>
            )}
        </div>
        </div>
      </div>

      {/* Modals & overlays — lazy-mounted on demand */}
      {deepAnalysisCreator && (
        <AppErrorBoundary fallback={null}>
          <React.Suspense fallback={null}>
            <CreatorInsightsDrawer />
          </React.Suspense>
        </AppErrorBoundary>
      )}
      {convCreator && (
        <AppErrorBoundary fallback={null}>
          <React.Suspense fallback={null}>
            <ReviewThreadDrawer />
          </React.Suspense>
        </AppErrorBoundary>
      )}

      {/* Non-blocking creator-import progress/result, pinned corner-of-page so it
          survives tab switches. Self-gates on the admin lane (every admin-side
          viewer on the campaign sees it — not just the uploader), matching the
          campaign-scoped, admin-only SSE fan-out. */}
      <ImportProgressPopup />
      <ReportExportNotifier />
    </>
  );
}
